// src/context/DarkModeContext.tsx
/**
 * Dark Mode Context - Simplified Convex-First Architecture
 *
 * DESIGN PRINCIPLES:
 * 1. Convex is source of truth for authenticated users
 * 2. localStorage is fallback for unauthenticated/initial load (prevents flash)
 * 3. Minimal state management - derive everything from Convex subscription
 * 4. No manual debouncing - Convex handles batching
 * 5. Cross-tab sync is automatic via Convex subscriptions
 *
 * MIGRATION NOTE (January 2026):
 * Simplified from 420+ lines to ~150 lines by leveraging Convex's reactive subscriptions.
 * Removed manual debouncing, storage event listeners, and complex effect chains.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';

// ============================================================================
// Types
// ============================================================================

export type ThemePreference = 'light' | 'dark' | 'system';

interface DarkModeContextType {
  /** The computed dark mode state (true = dark, false = light) */
  darkMode: boolean;
  /** The user's theme preference setting */
  themePreference: ThemePreference;
  /** Set the theme preference (light/dark/system) */
  setThemePreference: (preference: ThemePreference) => void;
  /** @deprecated Use setThemePreference instead. Kept for backward compatibility */
  toggleDarkMode: () => void;
  /** Whether the context has been initialized */
  isInitialized: boolean;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

// ============================================================================
// Constants
// ============================================================================

const THEME_PREFERENCE_KEY = 'careercard_theme_preference';
const DARK_MODE_KEY = 'careercard_dark_mode'; // Legacy key for backward compat

// ============================================================================
// Helper Functions (SSR-safe)
// ============================================================================

function getOSPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveTheme(mode: ThemePreference, osPrefersDark: boolean): 'light' | 'dark' {
  if (mode === 'system') {
    return osPrefersDark ? 'dark' : 'light';
  }
  return mode;
}

function loadFromLocalStorage(): ThemePreference | null {
  if (typeof window === 'undefined') return null;

  const stored = localStorage.getItem(THEME_PREFERENCE_KEY);
  if (stored && ['light', 'dark', 'system'].includes(stored)) {
    return stored as ThemePreference;
  }

  // Legacy fallback
  const legacyDarkMode = localStorage.getItem(DARK_MODE_KEY);
  if (legacyDarkMode === 'true') return 'dark';
  if (legacyDarkMode === 'false') return 'light';

  return null;
}

function saveToLocalStorage(mode: ThemePreference, resolvedTheme: 'light' | 'dark'): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(THEME_PREFERENCE_KEY, mode);
  localStorage.setItem(DARK_MODE_KEY, resolvedTheme === 'dark' ? 'true' : 'false');
}

function applyThemeToDOM(resolvedTheme: 'light' | 'dark'): void {
  if (typeof window === 'undefined') return;

  // Apply .dark class for Tailwind CSS dark mode
  if (resolvedTheme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }

  // Also set data-theme attribute for custom CSS
  document.documentElement.setAttribute('data-theme', resolvedTheme);
}

// ============================================================================
// Provider Component
// ============================================================================

export const DarkModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  // Convex subscription - source of truth for authenticated users
  const convexSettings = useQuery(
    api.settings.get,
    isAuthenticated ? {} : 'skip'
  );
  const updateSettingsMutation = useMutation(api.settings.update);

  // Local state for immediate UI response (before Convex confirms)
  // Initialize from localStorage to prevent flash on page load
  const [localPreference, setLocalPreference] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return 'system';
    return loadFromLocalStorage() ?? 'system';
  });

  // Track OS preference for 'system' mode
  const [osPrefersDark, setOsPrefersDark] = useState(() => getOSPrefersDark());

  // Derive the effective theme preference
  // Priority: Convex (if authenticated and loaded) > localStorage
  const themePreference = useMemo((): ThemePreference => {
    if (isAuthenticated && convexSettings !== undefined && convexSettings !== null) {
      // Use Convex as source of truth
      const dbTheme = convexSettings.theme as ThemePreference | undefined;
      if (dbTheme && ['light', 'dark', 'system'].includes(dbTheme)) {
        return dbTheme;
      }
    }
    // Fall back to local preference
    return localPreference;
  }, [isAuthenticated, convexSettings, localPreference]);

  // Compute resolved theme and darkMode boolean
  const resolvedTheme = useMemo(
    () => resolveTheme(themePreference, osPrefersDark),
    [themePreference, osPrefersDark]
  );
  const darkMode = resolvedTheme === 'dark';

  // Track initialization
  const [isInitialized, setIsInitialized] = useState(false);

  // ============================================================================
  // Effects
  // ============================================================================

  // Effect 1: Initialize and apply theme immediately on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Apply theme to DOM immediately to prevent flash
    applyThemeToDOM(resolvedTheme);
    setIsInitialized(true);
  }, []); // Only run once on mount

  // Effect 2: Apply theme to DOM when resolved theme changes
  useEffect(() => {
    applyThemeToDOM(resolvedTheme);
    saveToLocalStorage(themePreference, resolvedTheme);
  }, [resolvedTheme, themePreference]);

  // Effect 3: Sync local preference when Convex data loads
  useEffect(() => {
    if (isAuthenticated && convexSettings !== undefined && convexSettings !== null) {
      const dbTheme = convexSettings.theme as ThemePreference | undefined;
      if (dbTheme && ['light', 'dark', 'system'].includes(dbTheme)) {
        setLocalPreference(dbTheme);
      }
    }
  }, [isAuthenticated, convexSettings]);

  // Effect 4: Listen to OS theme changes (only matters when preference is 'system')
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (themePreference !== 'system') return; // Only listen when in 'system' mode

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setOsPrefersDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  // ============================================================================
  // Public API
  // ============================================================================

  const setThemePreference = useCallback((newMode: ThemePreference) => {
    // Update local state immediately for responsive UI
    setLocalPreference(newMode);

    // Persist to Convex if authenticated
    if (isAuthenticated) {
      updateSettingsMutation({ theme: newMode }).catch((error) => {
        console.error('[DarkMode] Failed to save theme preference:', error);
      });
    }

    // Always save to localStorage as fallback
    const resolved = resolveTheme(newMode, getOSPrefersDark());
    saveToLocalStorage(newMode, resolved);
  }, [isAuthenticated, updateSettingsMutation]);

  const toggleDarkMode = useCallback(() => {
    const newMode: ThemePreference = resolvedTheme === 'dark' ? 'light' : 'dark';
    setThemePreference(newMode);
  }, [resolvedTheme, setThemePreference]);

  // ============================================================================
  // Render
  // ============================================================================

  const value = useMemo(
    () => ({
      darkMode,
      themePreference,
      setThemePreference,
      toggleDarkMode,
      isInitialized,
    }),
    [darkMode, themePreference, setThemePreference, toggleDarkMode, isInitialized]
  );

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};
